import './prompts.css';
import { makeOffer, withdrawListing } from '../../api/rpc';
import { toRpcError } from '../../api/errors';
import type { GameState, StatePrompt } from '../../api/types';
import { serverNow } from '../../core/clock';
import { deckle } from '../../core/deckle';
import { geo } from '../../core/geo';
import { promptsLockRemaining } from '../../core/unlock';
import type { Tab, TabContext, TabFactory, TabShowOptions } from '../contract';
import { btn, clear, h } from './dom';
import { buildExchange } from './exchange';
import {
  buildBundles,
  completedAreas,
  errorText,
  hereAreaId,
  newlyHeld,
  type PairView,
  receivedCopies,
  viewSignature,
} from './logic';
import { Pager } from './pages';
import { Ticker } from './ticker';
import { buildArea, buildSelect, type ViewEnv } from './views';
import type { MyListing } from '../../api/types';

type View = { kind: 'select' } | { kind: 'area'; areaId: string } | { kind: 'exchange' };

const PAINTS = ['red', 'amber', 'blue', 'green'] as const;

export const createPromptsTab: TabFactory = (ctx: TabContext): Tab => {
  const el = h('div', 'pr-root');
  const sheet = h('div', 'pr-sheet');
  const face = h('div', 'pr-face');
  const scroll = h('div', 'pr-scroll');
  face.appendChild(scroll);
  sheet.appendChild(face);
  el.appendChild(sheet);

  // 素材（切手・消印・紐）は CSS の変数で渡す
  for (const p of PAINTS) {
    el.style.setProperty(`--pr-st-${p}`, `url(${ctx.asset(`prompts/stamp_${p}.png`)})`);
    el.style.setProperty(`--pr-qf-${p}`, `url(${ctx.asset(`prompts/stamp_flower_${p}.png`)})`);
  }
  el.style.setProperty('--pr-pm', `url(${ctx.asset('prompts/postmark.png')})`);
  el.style.setProperty('--pr-tw-h', `url(${ctx.asset('prompts/twine_h.png')})`);
  el.style.setProperty('--pr-tw-v', `url(${ctx.asset('prompts/twine_v.png')})`);
  el.style.setProperty('--pr-tw-k', `url(${ctx.asset('prompts/twine_knot.png')})`);

  const ticker = new Ticker();
  let state: GameState | null = null;
  let view: View = { kind: 'select' };
  let visible = false;
  let dirty = true;
  let sig = '';
  let hereId: string | null = null;
  let deal = false;
  let flyEl: HTMLElement | null = null;
  let lockEl: HTMLElement | null = null;
  const timers: ReturnType<typeof setTimeout>[] = [];

  const fresh = new Set<number>();
  const nowAreas = new Map<string, number>();
  const answers = new Map<number, string>();
  const partners = new Map<number, string>();
  const offered = new Map<number, PairView>();
  const picks = new Map<number, number>();
  const selfReceived = new Set<number>();
  let openListingId: number | null = null;
  let settled: { listing: MyListing; receivedId: number } | null = null;

  const later = (f: () => void, ms: number): void => {
    timers.push(setTimeout(f, ms));
  };

  const pager = new Pager({
    ctx,
    ticker,
    state: () => state,
    markFresh: (ids) => ids.forEach((i) => fresh.add(i)),
    rememberAnswer: (id, t) => answers.set(id, t),
    onClosed: () => {
      dirty = true;
      render();
    },
  });
  el.appendChild(pager.el);

  function cutSheet(): void {
    if (face.offsetWidth > 0) face.style.setProperty('--deckle', deckle(face.offsetWidth, face.offsetHeight, 5113));
    pager.cut();
  }
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(() => cutSheet()).observe(face);

  function viewEnv(): ViewEnv {
    const nowSet = new Set<string>();
    const t = Date.now();
    for (const [k, until] of nowAreas) if (until > t) nowSet.add(k);
    return {
      state: state as GameState,
      hereId,
      ticker,
      fresh,
      nowAreas: nowSet,
      deal,
      answers,
      partners,
      openArea: (areaId) => go({ kind: 'area', areaId }),
      openExchange: () => go({ kind: 'exchange' }),
      openPrompt,
      onQuizWaitEnd: () => {
        dirty = true;
        render();
      },
    };
  }

  function go(v: View): void {
    view = v;
    settled = view.kind === 'exchange' ? settled : null;
    dirty = true;
    render();
    scroll.scrollTop = 0;
  }

  function openPrompt(p: StatePrompt, b: ReturnType<typeof buildBundles>[number]): void {
    if (p.prompt_type === 'quiz') pager.openQuiz(p, b);
    else pager.openPhoto(p, b);
  }

  function render(): void {
    if (!visible || !state || !dirty) return;
    dirty = false;
    ticker.clear();
    const top = scroll.scrollTop;
    clear(scroll);
    const env = viewEnv();
    const lock = promptsLockRemaining(state);
    face.classList.toggle('pr-locked', lock > 0);
    lockEl?.remove();
    lockEl = null;
    if (lock > 0) {
      // 解禁前（024）。お題の束は灰色で見せるだけで押せない
      view = { kind: 'select' };
      scroll.appendChild(buildSelect(env));
      lockEl = buildLock(lock);
      face.appendChild(lockEl);
    } else if (view.kind === 'select') {
      scroll.appendChild(buildSelect(env));
    } else if (view.kind === 'area') {
      const b = buildBundles(state, hereId).find((x) => x.area.area_id === (view as { areaId: string }).areaId);
      if (!b) {
        view = { kind: 'select' };
        scroll.appendChild(buildSelect(env));
      } else {
        scroll.appendChild(buildArea(b, env, () => go({ kind: 'select' })));
      }
    } else {
      scroll.appendChild(
        buildExchange({
          state,
          ticker,
          offered,
          openId: openListingId,
          picks,
          settled,
          fresh,
          onBack: () => go({ kind: 'select' }),
          onToggle: (id) => {
            openListingId = openListingId === id ? null : id;
            dirty = true;
            render();
          },
          onPick: (id, pid) => picks.set(id, pid),
          onOffer: offer,
          onWithdraw: withdraw,
          onOpenPost: () => pager.openPost(),
          onCooldownEnd: () => {
            dirty = true;
            render();
          },
        }),
      );
    }
    scroll.scrollTop = top;
    cutSheet();
    deal = false;
    // 動きは1回だけ。次の描き直しでは繰り返さない
    if (fresh.size > 0) later(() => fresh.clear(), 1500);
    if (nowAreas.size > 0) later(() => nowAreas.clear(), 2500);
  }

  function buildLock(n: number): HTMLElement {
    const box = h('div', 'pr-lock', undefined, { role: 'status' });
    const big = h('p', 'pr-lk-n');
    big.append('あと', h('b', 'pr-num', String(n)), '個クリアで解禁');
    box.append(
      big,
      h('p', 'pr-note', 'スポットをクリアすると、お題を集められるようになります。'),
      btn('pr-lk-go', '図鑑を開く', () => ctx.switchTab('book')),
    );
    return box;
  }

  async function offer(pv: PairView, give: number): Promise<string | null> {
    try {
      const r = await makeOffer(pv.listing.listing_id, give);
      selfReceived.add(r.received_prompt_id);
      partners.set(r.received_prompt_id, pv.listing.team_name);
      offered.set(pv.listing.listing_id, pv);
      fresh.add(r.received_prompt_id);
      openListingId = null;
      await ctx.refresh();
      dirty = true;
      render();
      // 1.6秒後に一覧から消える
      later(() => {
        offered.delete(pv.listing.listing_id);
        dirty = true;
        render();
      }, 1600);
      return null;
    } catch (e) {
      const code = toRpcError(e).code;
      if (code === 'listing_not_found' || code === 'duplicate_card') void ctx.refresh();
      return errorText(code);
    }
  }

  async function withdraw(): Promise<string | null> {
    try {
      await withdrawListing();
      await ctx.refresh();
      return null;
    } catch (e) {
      const code = toRpcError(e).code;
      void ctx.refresh();
      return errorText(code);
    }
  }

  function updateHere(): boolean {
    if (!state) return false;
    const id = hereAreaId(state, geo.fresh());
    if (id === hereId) return false;
    hereId = id;
    return true;
  }
  geo.changed.on(() => {
    if (updateHere()) {
      dirty = true;
      render();
    }
  });

  function playOpen(): Promise<void> {
    return new Promise((resolve) => {
      view = { kind: 'select' };
      dirty = true;
      const tab = document.querySelector<HTMLElement>('.sh-t-prompts');
      const er = el.getBoundingClientRect();
      const tr = tab?.getBoundingClientRect();
      if (!tr || er.width === 0) {
        render();
        resolve();
        return;
      }
      sheet.classList.add('pr-hid');
      render();
      const sr = sheet.getBoundingClientRect();
      flyEl?.remove();
      const fly = h('div', 'pr-fly');
      flyEl = fly;
      const box = (r: { left: number; top: number; width: number; height: number }): Keyframe => ({
        left: `${r.left - er.left}px`,
        top: `${r.top - er.top}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
      });
      el.appendChild(fly);
      fly.animate([box(tr), box(sr)], { duration: 380, easing: 'cubic-bezier(.3,.7,.4,1)', fill: 'forwards' });
      later(() => {
        // 台紙が伸びたあと、束を下部タブから1つずつ置く
        deal = true;
        dirty = true;
        sheet.classList.remove('pr-hid');
        scroll.classList.add('pr-fadein');
        render();
        later(() => {
          fly.remove();
          scroll.classList.remove('pr-fadein');
        }, 60);
        later(resolve, 800);
      }, 380);
    });
  }

  function absorb(next: GameState, prev: GameState | null): void {
    // 解禁した瞬間（024）。どのタブにいても知らせる
    if (promptsLockRemaining(prev) > 0 && promptsLockRemaining(next) === 0) {
      ctx.notify({ heading: 'お題', body: 'お題が解禁されました' });
    }
    // 手に入ったお題は、見ていれば絵葉書になる動き（㊴のA）
    for (const p of newlyHeld(prev, next)) fresh.add(p.prompt_id);
    // コンプ（㊲）
    for (const id of completedAreas(prev, next)) nowAreas.set(id, Date.now() + 30_000);
    // 自分の掲示に他チームが応じた（㊸①）
    const copies = receivedCopies(prev, next).filter((c) => !selfReceived.has(c.prompt_id));
    for (const c of copies) {
      const partner = partners.get(c.prompt_id);
      ctx.notify({ heading: partner ? `交換所・${partner}と交換` : '交換所・交換が成立', body: `${c.title}が届きました` });
    }
    if (prev?.exchange.my_listing && !next.exchange.my_listing && copies.length > 0) {
      settled = { listing: prev.exchange.my_listing, receivedId: copies[0]?.prompt_id ?? -1 };
    } else if (next.exchange.my_listing) {
      settled = null;
    }
    for (const c of copies) selfReceived.delete(c.prompt_id);
  }

  return {
    id: 'prompts',
    el,
    async show(opts: TabShowOptions) {
      visible = true;
      ticker.start();
      const animate = opts.animate && !opts.focus;
      if (state) updateHere();
      const f = promptsLockRemaining(state) > 0 ? undefined : opts.focus;
      if (f && state) {
        if (f.promptId !== undefined) {
          const p = state.prompts.find((x) => x.prompt_id === f.promptId);
          if (p) {
            view = { kind: 'area', areaId: p.area_id };
            dirty = true;
            render();
            const b = buildBundles(state, hereId).find((x) => x.area.area_id === p.area_id);
            if (b && p.held === null && !p.blocked) openPrompt(p, b);
            return;
          }
        }
        if (f.areaId) view = { kind: 'area', areaId: f.areaId };
      }
      if (animate && state) {
        await playOpen();
        return;
      }
      dirty = true;
      render();
    },
    hide() {
      visible = false;
      ticker.stop();
      timers.splice(0).forEach(clearTimeout);
      flyEl?.remove();
      sheet.classList.remove('pr-hid');
      if (pager.isOpen && !pager.isBusy) pager.close();
    },
    update(next, prev) {
      absorb(next, prev);
      const first = state === null;
      state = next;
      const minute = Math.floor(serverNow() / 60_000);
      const s = `${viewSignature(next)}|${minute}|${promptsLockRemaining(next)}`;
      const here = updateHere();
      if (s !== sig || here || first || fresh.size > 0 || nowAreas.size > 0) {
        sig = s;
        dirty = true;
        // 開いている頁があれば、その下の画面だけ描き直す
        render();
      }
    },
  };
};
